import React from "react";
import SEO from "@/components/SEO";
import { COMPANY } from "@/data/site";
import { Link } from "react-router-dom";

const PrivacyPolicyPage = () => (
  <>
    <SEO
      title="Privacy Policy | Crosstown Movers"
      description="How Crosstown Movers collects, uses, and protects the personal information you share when requesting a moving quote in Wyoming."
      path="/privacy-policy"
    />
    <section className="py-20 md:py-28 bg-[var(--ct-primary)] text-white">
      <div className="section-container">
        <div className="label-xs text-white mb-3">Legal</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
          Privacy Policy
        </h1>
        <p className="mt-4 text-white/80 max-w-3xl">Last updated: January 2026</p>
      </div>
    </section>

    <section className="py-16 bg-white">
      <article className="section-container max-w-3xl space-y-8">
        <div>
          <p className="text-[var(--ct-text-muted)] leading-relaxed">
            Crosstown Movers ("we", "us", or "our") respects your privacy. This Privacy
            Policy explains what information we collect when you visit
            crosstownmoversllc.com or request a moving quote, how we use it, and the
            choices you have.
          </p>
        </div>

        <Section title="Information We Collect">
          <p>We collect information you voluntarily provide when you:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Submit the Instant Quote wizard or quote form</li>
            <li>Call or text our business phone</li>
            <li>Email us directly</li>
          </ul>
          <p className="mt-3">This information may include your name, phone number, email address, moving origin and destination, preferred move date, and any details about your move that you choose to share (size, special items, packing needs).</p>
        </Section>

        <Section title="How We Use Your Information">
          <p>We use the information you provide solely to:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Respond to your quote request and follow up about your move</li>
            <li>Send SMS confirmations, reminders, and scheduling updates related to your move</li>
            <li>Coordinate moving-day logistics</li>
            <li>Improve our service quality and customer experience</li>
          </ul>
        </Section>

        <Section title="SMS / Text Messaging">
          <p>
            By submitting your phone number on our site, you consent to receive SMS
            messages from Crosstown Movers about your quote request and move. Message
            frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to
            opt out at any time, or <strong>HELP</strong> for assistance.
          </p>
          <p className="mt-3">
            <strong>We will never share, sell, or rent your mobile phone number, or any
            consent for SMS messaging, with third parties for their marketing purposes.</strong>
          </p>
          <p className="mt-3">
            Full SMS terms are available at{" "}
            <Link to="/sms-terms" className="font-semibold text-[var(--ct-primary)] underline">
              /sms-terms
            </Link>.
          </p>
        </Section>

        <Section title="Information Sharing">
          <p>We do not sell, trade, or rent your personal information to third parties. We may share information only:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>With service providers who help us operate our business (e.g., Twilio for SMS delivery, MongoDB for secure data storage), under strict confidentiality terms</li>
            <li>If required by law, court order, or to protect our legal rights</li>
            <li>In connection with a business transfer (merger, acquisition, sale of assets), in which case we will notify you</li>
          </ul>
        </Section>

        <Section title="Data Security">
          <p>
            We use reasonable administrative, technical, and physical safeguards to
            protect your information from unauthorized access, alteration, disclosure,
            or destruction. However, no method of transmission over the internet is
            100% secure.
          </p>
        </Section>

        <Section title="Data Retention">
          <p>
            We retain your information only as long as needed to fulfill the purposes
            described in this policy, comply with our legal obligations, resolve
            disputes, and enforce our agreements. You may request deletion of your
            information at any time by contacting us at the details below.
          </p>
        </Section>

        <Section title="Cookies & Analytics">
          <p>
            Our website may use cookies and analytics tools to understand how visitors
            use the site. These are not used for advertising or to identify you
            personally. You can disable cookies in your browser settings.
          </p>
        </Section>

        <Section title="Your Choices">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Opt out of SMS:</strong> reply STOP to any message</li>
            <li><strong>Update or delete your info:</strong> contact us at the details below</li>
            <li><strong>Don't submit a quote form</strong> if you don't want to share your information</li>
          </ul>
        </Section>

        <Section title="Children's Privacy">
          <p>
            Our services are not directed to children under 13. We do not knowingly
            collect personal information from children. If you believe a child has
            provided us with information, please contact us and we will delete it.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. The "Last updated"
            date at the top reflects the most recent changes. Continued use of our site
            after changes are posted means you accept the updated policy.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>
            Questions about this Privacy Policy or how we handle your information?
          </p>
          <div className="mt-3 p-5 rounded-2xl bg-[var(--ct-accent)] text-[var(--ct-primary)]">
            <p className="m-0"><strong>Crosstown Movers</strong></p>
            <p className="m-0">{COMPANY.address}</p>
            <p className="m-0">
              Phone:{" "}
              <a href={`tel:${COMPANY.phoneTel}`} className="font-semibold underline">
                {COMPANY.phone}
              </a>
            </p>
            <p className="m-0">
              Email:{" "}
              <a href={`mailto:${COMPANY.email}`} className="font-semibold underline">
                {COMPANY.email}
              </a>
            </p>
          </div>
        </Section>
      </article>
    </section>
  </>
);

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-2xl font-semibold text-[var(--ct-primary)]" style={{ fontFamily: "Outfit" }}>
      {title}
    </h2>
    <div className="mt-3 text-[var(--ct-text-muted)] leading-relaxed">{children}</div>
  </div>
);

export default PrivacyPolicyPage;
